"use client";

/**
 * Cabeçalho do agendamento em etapas: trilha de progresso + título animado.
 *
 * Etapas que não são a atual ficam só como um círculo com ícone (sem rótulo),
 * bem mais compacto — 6 cabem numa tela de 375px sem precisar rolar. Só a
 * etapa atual vira uma pill com ícone + texto. A barra de rolagem nativa some
 * (classe `.no-scrollbar` em app/layout.tsx); quando ela ainda é necessária
 * (tela muito estreita ou muitas etapas), a etapa atual é trazida para a
 * vista automaticamente ao trocar.
 *
 * O título recebe `key={currentStep.id}`: a troca de etapa remonta o bloco e a
 * animação `animate-fade-in` (definida em app/layout.tsx) roda de novo. Sem a
 * key a animação só apareceria no primeiro render.
 */

import { useEffect, useRef } from "react";
import {
  FiCalendar,
  FiCheck,
  FiClipboard,
  FiScissors,
  FiShoppingBag,
  FiUserPlus,
  FiUsers,
} from "react-icons/fi";
import type { IconType } from "react-icons";
import type { BookingWizardStep, BookingWizardStepId } from "../utils/bookingWizard";

interface BookingWizardHeaderProps {
  steps: BookingWizardStep[];
  currentIndex: number;
  maxReachableIndex: number;
  progress: { current: number; total: number; percent: number };
  onStepSelect: (stepId: BookingWizardStepId) => void;
  isStepComplete: (stepId: BookingWizardStepId) => boolean;
}

const STEP_ICONS: Record<BookingWizardStepId, IconType> = {
  provider: FiUsers,
  service: FiScissors,
  schedule: FiCalendar,
  showcase: FiShoppingBag,
  account: FiUserPlus,
  details: FiClipboard,
};

export function BookingWizardHeader({
  steps,
  currentIndex,
  maxReachableIndex,
  progress,
  onStepSelect,
  isStepComplete,
}: BookingWizardHeaderProps) {
  const currentStep = steps[currentIndex];
  const currentPillRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    currentPillRef.current?.scrollIntoView?.({
      behavior: "smooth",
      inline: "center",
      block: "nearest",
    });
  }, [currentIndex]);

  if (!currentStep) return null;

  return (
    <div className="border-b border-slate-200/80 bg-white">
      <div className="px-4 pt-4 sm:px-6 sm:pt-5">
        <div className="flex items-center justify-between gap-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-primary-700">
            Etapa {progress.current} de {progress.total}
          </p>
          <p className="text-xs font-medium text-slate-500">{currentStep.label}</p>
        </div>

        <div
          className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-slate-100"
          role="progressbar"
          aria-valuemin={1}
          aria-valuemax={progress.total}
          aria-valuenow={progress.current}
          aria-label="Progresso do agendamento"
        >
          <div
            className="h-full rounded-full bg-gradient-to-r from-primary-500 to-primary-600 transition-all duration-500"
            style={{ width: `${progress.percent}%` }}
          />
        </div>

        <nav
          aria-label="Etapas do agendamento"
          className="no-scrollbar -mx-4 mt-3 flex items-center gap-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:flex-wrap sm:px-0"
        >
          {steps.map((step, index) => {
            const reachable = index <= maxReachableIndex;
            const isCurrent = index === currentIndex;
            const done = index < currentIndex && isStepComplete(step.id);
            const Icon = STEP_ICONS[step.id];

            return (
              <button
                key={step.id}
                ref={isCurrent ? currentPillRef : undefined}
                type="button"
                onClick={() => reachable && onStepSelect(step.id)}
                disabled={!reachable}
                aria-current={isCurrent ? "step" : undefined}
                aria-label={step.label}
                title={step.label}
                className={`inline-flex flex-shrink-0 items-center gap-2 rounded-full border font-semibold transition-all duration-300 focus:outline-none focus-visible:ring-4 focus-visible:ring-primary-100 ${
                  isCurrent
                    ? "animate-scale-in border-primary-500 bg-primary-600 py-1.5 pl-1.5 pr-3.5 text-xs text-white shadow-sm shadow-primary-600/20"
                    : "h-9 w-9 justify-center text-[13px] " +
                      (done
                        ? "border-primary-200 bg-primary-50 text-primary-700 hover:bg-primary-100"
                        : reachable
                          ? "border-slate-200 bg-white text-slate-500 hover:border-primary-200 hover:text-primary-700"
                          : "cursor-not-allowed border-slate-200 bg-slate-50 text-slate-300")
                }`}
              >
                <span
                  className={`inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full ${
                    isCurrent ? "bg-white/20 text-white" : ""
                  }`}
                >
                  {done ? (
                    <FiCheck className="h-3.5 w-3.5" aria-hidden="true" />
                  ) : (
                    <Icon className="h-3.5 w-3.5" aria-hidden="true" />
                  )}
                </span>
                {isCurrent && step.label}
              </button>
            );
          })}
        </nav>
      </div>

      <div key={currentStep.id} className="animate-fade-in px-4 pb-5 pt-4 sm:px-6">
        <h2 className="text-2xl font-bold leading-tight text-[#233055] sm:text-3xl">
          {currentStep.title}
        </h2>
        <p className="mt-1.5 text-sm leading-6 text-slate-600">{currentStep.subtitle}</p>
      </div>
    </div>
  );
}
